import { store, useAppSelector } from "@/app/store";
import Button from "@/components/Common/Button";
import FlexRow from "@/components/Common/FlexRow";
import Grid from "@/components/Common/Grid";
import { auditInfo, auditOperationLabel } from "@/features/audit/auditSlice";
import { Modal, ModalContent, ModalBody } from "@heroui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AuditSearch, { AuditSearchParams, AuditSearchFormState, initialSearchState } from "./AuditSearch";

const Audit=()=>{
    const {t}=useTranslation();
    const [cols, setCols] = useState([] as any[]);
    const [rows,setRows]=useState([] as auditInfo[])
    const [isSearchOpen, setIsSearchOpen] = useState(false)
    const [csvExport, setCsvExport] = useState(0)
    const audit=useAppSelector((state)=>state.audit)

    // Search form state - persisted across modal open/close
    const [formState, setFormState] = useState<AuditSearchFormState>(initialSearchState);

    useEffect(()=>{
        store.getAudit()
    },[])

    const formatTimestamp = (params: any) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        return date.toLocaleString();
    };

    useEffect(()=>{
        setCols([
            {
                field: "timestamp",
                headerName: t("audit:timestamp"),
                flex: 1,
                width: 180,
                minWidth: 180,
                valueFormatter: formatTimestamp,
                pinned: 'left',
                lockPinned: true,
                cellStyle: { backgroundColor: '#ffffff' },
            },
            {
                field: "user",
                headerName: t("audit:user"),
                flex: 1,
                minWidth: 120,
            },
            {
                field: "ip",
                headerName: t("audit:ip"),
                flex: 1,
                minWidth: 130,
            },
            {
                field: "type",
                headerName: t("audit:type"),
                flex: 1,
                minWidth: 100,
            },
            {
                field: "name",
                headerName: t("audit:desc"),
                flex: 1,
                minWidth: 150,
                valueGetter: (params: any) => {
                    const desc = params.data?.desc || "";
                    const id = params.data?.id || "";
                    if (desc && id) return `${desc}(${id})`;
                    if (desc) return desc;
                    if (id) return id;
                    return "";
                },
            },
            {
                field: "secondary_name",
                headerName: t("audit:secondary_id"),
                flex: 1,
                minWidth: 150,
                valueGetter: (params: any) => {
                    const desc = params.data?.secondary_desc || "";
                    const id = params.data?.secondary_id || "";
                    if (desc && id) return `${desc}(${id})`;
                    if (desc) return desc;
                    if (id) return id;
                    return "";
                },
            },
            {
                field: "operation",
                headerName: t("audit:operation"),
                flex: 1,
                // The checkout labels below are sentences, not one-word verbs
                // ("Checkout accepted by TV"), and at 100 they were cut to
                // "Checko…" — which tells nobody which of the seven it was.
                minWidth: 190,
                // MW light adds checkout_request / _cancel / _ack / _done /
                // _failed / _unsupported / _denied (#91 §2.7). Labelled here;
                // anything unmapped still shows its raw name.
                valueGetter: (params: any) => auditOperationLabel(params.data?.operation),
                tooltipValueGetter: (params: any) => auditOperationLabel(params.data?.operation),
            },
            {
                field: "action",
                headerName: t("audit:action"),
                flex: 1,
                minWidth: 100,
            },
            {
                field: "field",
                headerName: t("audit:field"),
                flex: 1,
                minWidth: 200,
            },
            {
                field: "previous",
                headerName: t("audit:previous"),
                flex: 1,
                minWidth: 100,
                valueGetter: (params: any) => {
                    const prev = params.data?.previous || "";
                    const prevdesc = params.data?.previous_desc || "";
                    if (prev && prevdesc) return `${prevdesc}(${prev})`;
                    if (prev) return prev;
                    if (prevdesc) return prevdesc;
                    return "";
                },
            },
            {
                field: "current",
                headerName: t("audit:current"),
                flex: 1,
                minWidth: 100,
                valueGetter: (params: any) => {
                    const current = params.data?.current || "";
                    const currentDesc = params.data?.current_desc || "";
                    if (current && currentDesc) return `${currentDesc}(${current})`;
                    if (current) return current;
                    if (currentDesc) return currentDesc;
                    return "";
                },
            },
            {
                field: "result",
                headerName: t("audit:result"),
                flex: 1,
                minWidth: 100,
            },
        ])
    },[t])

    useEffect(()=>{
        setRows(audit.entries || [])
    },[audit.entries])

    const handleSearch = (params: AuditSearchParams) => {
        store.getAudit(params);
        setIsSearchOpen(false);
    };

    const handleCancel = () => {
        setIsSearchOpen(false);
    };

    return(
    <React.Fragment>
    <FlexRow direction="column"
	         align="start"
	         className={[
               'bg-white',
               'px-[32px] pt-[32px] sticky top-[64px] z-10',
             ]}>
      <div className="text-[32px]/[48px]">
        { t('audit:caption') }
      </div>
      <FlexRow className="w-full justify-between mt-[20px]">
        <Button caption={t('audit:search')}
                className="uppercase"
                icon="search"
                onClick={() => setIsSearchOpen(true)}
                layout="white" />
        {rows && rows.length > 0 &&
          <Button caption={t('channel_lists:options:export')}
                  className="uppercase"
                  icon="share"
                  onClick={() => setCsvExport(csvExport + 1)} />
        }
      </FlexRow>
    </FlexRow>
    <FlexRow direction="column"
             align="start"
             className={[
               "py-[16px]",
               "px-[32px]"
             ]}>
      <Grid rowData={rows}
            columnDefs={cols}
            filter={true}
            showPages={true}
            pageSize={10}
            csvExport={csvExport}
            csvFile="audit.csv" />
    </FlexRow>
    {isSearchOpen &&
        <Modal backdrop="opaque"
               isOpen={isSearchOpen}
               isDismissable={false}
               onOpenChange={setIsSearchOpen}
               classNames={{
                 backdrop: "bg-[#0000007f]",
                 wrapper: "justify-end"
               }}
               hideCloseButton={true}
               radius="none"
               placement="top">
          <ModalContent className="h-full !mx-0 !my-0 w-full max-w-[620px]">
            <ModalBody className="w-full overflow-auto">
              <FlexRow direction="column" 
                       flex={0}>
                <AuditSearch
                  onSearch={handleSearch}
                  onCancel={handleCancel}
                  formState={formState}
                  setFormState={setFormState}
                />
              </FlexRow>
            </ModalBody>
          </ModalContent>
        </Modal>
    }
    </React.Fragment>
    )
}

export default Audit
